import { StyleSheet } from 'aphrodite';
import { hexToRGBA } from '../../../../models/UiUtils';
import TileBackgroundImageUrl from '../../../assets/surveyTileBackground.png';
import { THEME } from '../../../components-v2/Theme/theme';
import {
	brandPrimary,
	brandPrimaryHover,
	charmGray,
	destructive,
	mention,
	successGreen,
	titles,
	white,
} from '../../../styles/colors';
import { CssSelectors, Layouts } from '../../../styles/styles';

const {
	typography: { fontSizes },
	spacing,
	colors,
	common,
} = THEME;

export const styleSheet = StyleSheet.create({
	annotation: {
		fontStyle: 'italic',
	},
	archivedLabel: {
		color: destructive,
		textTransform: 'uppercase',
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		marginBottom: spacing.xl,
		maxWidth: 500,
	},
	satisfactionSurveyContainer: {
		boxSizing: 'border-box',
		color: titles,
		display: 'flex',
		flexDirection: 'column',
		fontSize: fontSizes.sm,
		paddingBottom: spacing.lg,
		width: '100%',
		background: THEME.colors.background.light,
	},
	content: {
		boxSizing: 'border-box',
		padding: '0 12.5% 10% 12.5%',
	},
	copyLinkButton: {
		...Layouts.horizontalStack(10),
		alignItems: 'center',
		display: 'inline-flex',
	},
	checkboxLabel: {
		display: 'inline-flex',
		transform: 'translateY(-18px)',
	},
	deleteButton: {
		fontSize: 0,
		color: 'transparent',
		position: 'absolute',
		top: spacing.lg,
		right: 0,
	},
	disabled: {
		opacity: 0.5,
		pointerEvents: 'none',
	},
	divider: {
		border: THEME.colors.border.light,
	},
	fieldGroup: {
		...Layouts.verticalStack(spacing.md),
	},
	bottomSection: {
		width: '100%',
		boxSizing: 'border-box',
		background: white,
		boxShadow: `0px 0px 12px ${mention}`,
		marginBottom: spacing.lg,
		paddingBottom: spacing.md,
		borderTopLeftRadius: common.borderRadius,
		borderBottomLeftRadius: common.borderRadius,
	},
	header: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'space-between',
		padding: `${spacing.md}px 0 0 0`,
	},
	headerInner: {
		paddingLeft: spacing.md,
	},
	headerActions: {
		...Layouts.horizontalStack(30),
	},
	introTextArea: {
		height: 66,
	},
	constrainedFieldGroup: {
		...Layouts.verticalStack(spacing.md),
		maxWidth: 721,
	},
	thresholdInput: {
		width: 57,
		...CssSelectors.allDescendants(
			{
				width: 37,
			},
			'input'
		),
	},
	label: {
		color: charmGray,
		fontSize: 14,
	},
	layoutFlexRowSpaceBetween: {
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'row',
		gap: 24,
	},
	questionWrap: {
		position: 'relative',
		paddingRight: 40,
		marginBottom: spacing.md,
	},
	questionInput: {
		marginBottom: spacing.sm,
	},
	radioButtons: {
		...Layouts.verticalStack(spacing.sm),
	},
	reviewRequestLinkTypeInput: {
		width: '100%',
	},
	reviewRequestMessage: {
		height: 140,
	},
	section: {
		padding: `${spacing.md}px ${spacing.md}px 0`,
		...CssSelectors.allChildren(
			{
				marginBottom: spacing.md,
			},
			':not(:last-child)'
		),
	},
	sectionNoBorderTop: {
		borderTop: spacing.none,
	},
	sectionFullWidth: {
		width: '100%',
		boxSizing: 'border-box',
		marginBottom: spacing.md,
		position: 'relative',
	},
	sectionTitle: {
		color: titles,
		fontSize: fontSizes.lg,
	},
	starRating: {
		...Layouts.horizontalStack(4),
		fontSize: fontSizes.lg,
	},
	checkboxText: {
		...Layouts.horizontalStack(4),
		fontSize: fontSizes.sm,
		display: 'inline-flex',
	},
	toggleText: {
		whiteSpace: 'nowrap',
	},
	addQuestionButton: {
		color: brandPrimary,
		padding: spacing.md,
		width: '100%',
		border: `2px dashed ${brandPrimary}`,
		borderRadius: common.borderRadius,
		textAlign: 'center',
		background: hexToRGBA(brandPrimary, 0.1),
		transition: 'background 300ms ease-in-out',
		':hover': {
			background: hexToRGBA(brandPrimary, 0.2),
		},
		':focus': {
			background: hexToRGBA(brandPrimary, 0.2),
		},
	},
	fieldset: {
		border: spacing.none,
	},
	errors: {
		color: destructive,
		fontSize: fontSizes.xs,
		margin: spacing.none,
	},
	previewButton: {
		color: brandPrimary,
		fontSize: fontSizes.sm,
		...CssSelectors.allChildren(
			{
				display: 'flex',
				alignItems: 'center',
				gap: spacing.sm,
			},
			'span'
		),
	},
	editPen: {
		position: 'absolute',
		top: spacing.none,
		right: -spacing.xl,
	},
	headerNameWrap: {
		height: 59,
	},
	headerTitle: {
		fontSize: fontSizes.lg,
		lineHeight: '22px', // must be in px
		color: titles,
		margin: spacing.none,
		paddingBottom: spacing.none,
		maxWidth: 725,
	},
	headerNameInputWrap: {
		minHeight: 50,
		marginBottom: spacing.sm,
	},
	formMenu: {
		display: 'flex',
		gap: spacing.md,
		alignItems: 'center',
	},
	submitButton: {},
	previewLink: {
		textDecoration: 'none',
		color: brandPrimary,
		fontSize: fontSizes.sm,
		display: 'flex',
		gap: spacing.sm,
		alignItems: 'center',
		'&:hover': {
			color: brandPrimaryHover,
		},
	},
	tabViewContainer: {
		position: 'relative',
		zIndex: 1,
		height: `calc(100vh - 185px)`,
	},
	tabList: {
		position: 'relative',
		zIndex: 1,
		background: white,
		borderBottom: common.border,
		display: 'flex',
		flexDirection: 'row',
		gap: spacing.sm,
		marginBottom: spacing.sm,
		minHeight: spacing.xl,
		...CssSelectors.allDescendants(
			{
				width: 'auto',
				flex: 0,
				textTransform: 'capitalize',
				fontSize: fontSizes.sm,
				padding: `${spacing.sm}px ${spacing.md}px`,
				display: 'flex',
				alignItems: 'center',
			},
			'button'
		),
	},
	tabTextError: {
		color: `${destructive} !important`,
	},
	bodyBackground: {
		backgroundImage: `url(${TileBackgroundImageUrl})`,
		backgroundRepeat: 'repeat',
		backgroundSize: 60,
		bottom: 0,
		content: '',
		display: 'block',
		height: '100%',
		left: 0,
		opacity: 0.2,
		position: 'absolute',
		right: 0,
		top: 0,
		width: '100%',
		zIndex: 0,
	},
	formContainer: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		justifyContent: 'flex-start',
		width: '100%',
		alignItems: 'center',
		padding: '40px 0',
	},
	additionalQuestionsContainer: {
		width: '100%',
		maxWidth: 766,
		position: 'relative',
		zIndex: 1,
	},
	sectionHeader: {
		boxShadow: `0px 0px 12px ${mention}`,
		marginBottom: spacing.lg,
	},
	additionalRecipient: {
		marginBottom: spacing.md,
		width: '100%',
		...CssSelectors.allDescendants(
			{
				...CssSelectors.allChildren(
					{
						width: '100%',
						...CssSelectors.allDescendants(
							{
								color: colors.text.light,
							},
							'.text-input-form-field-label'
						),
					},
					'div:first-child'
				),
			},
			'div'
		),
	},
	removeButton: {
		color: 'transparent',
		...CssSelectors.allChildren(
			{
				...CssSelectors.allDescendants(
					{
						color: 'transparent',
						fontSize: 0,
					},
					'span'
				),
			},
			'span'
		),
	},
	removeButtonIcon: {
		width: spacing.md,
		height: spacing.md,
		color: colors.text.light,
	},
	sectionDescription: {
		color: colors.text.light,
		margin: `0 0 ${spacing.md}px 0`,
	},
	notificationEmail: {
		width: '100%',
		...CssSelectors.allDescendants(
			{
				fontSize: fontSizes.sm,
			},
			'.notificationEmail'
		),
	},
	tabContent: {
		background: THEME.colors.background.light,
		margin: `0 -${spacing.md}px`,
		padding: `0 ${spacing.md}px`,
		overflowY: 'auto',
	},
	newTabContent: {
		flexGrow: 1,
	},
	additionalRecipientsCreator: {
		marginTop: `${spacing.md}px !important`,
		marginBottom: spacing.sm,
		...CssSelectors.allDescendants(
			{
				color: `${colors.text.light}`,
				display: 'flex',
				marginBottom: spacing.sm,
			},
			'label'
		),
	},
	additionalRecipientsLabel: {
		display: 'block',
		marginBottom: spacing.sm,
		color: `${colors.text.light}`,
	},
	notificationSection: {},
	notificationSettingsEnabled: {
		overflow: 'visible',
	},
	notificationSettingsDisabled: {
		height: 0,
		padding: 0,
		overflow: 'hidden',
	},
	reviewRequestSettingsEnabled: {
		overflow: 'visible',
		height: 'auto',
	},
	reviewRequestSettingsDisabled: {
		height: 0,
		padding: 0,
		overflow: 'hidden',
	},
	page: {
		background: THEME.colors.background.light,
	},
	removeQuestionButton: {
		transform: 'translateY(-52px)',
	},
	bottomSectionNotSaved: {
		borderLeft: `4px solid ${brandPrimary}`,
	},
	bottomSectionSaved: {
		borderLeft: `4px solid ${successGreen}`,
	},
	bottomSectionNoValue: {
		borderLeft: `4px solid ${destructive}`,
	},
	tagPrefixContainer: {
		display: 'flex',
		gap: spacing.sm,
		alignItems: 'center',
		justifyContent: 'flex-end',
		position: 'absolute',
		top: -40,
		right: spacing.xxxl,
		borderRight: common.border,
		paddingRight: spacing.sm,
		maxWidth: '60%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	tagPrefixButton: {
		fontSize: fontSizes.sm,
		padding: `${spacing.xs}px ${spacing.sm}px`,
		color: colors.primary.main,
		backgroundColor: 'transparent',
		borderRadius: common.borderRadius,
		'&:hover': {
			backgroundColor: hexToRGBA(brandPrimary, 0.1),
		},
	},
	afterInputIcon: {
		position: 'absolute',
		right: spacing.sm,
		top: '50%',
		transform: 'translateY(-50%)',
		pointerEvents: 'none',
	},
	hoverCardIcon: {
		position: 'absolute',
		right: spacing.sm,
		top: '50%',
		transform: 'translateY(-50%)',
		pointerEvents: 'auto',
		cursor: 'pointer',
		zIndex: 1,
		color: THEME.colors.pill.blue.icon,
	},
	conditionalCheckboxContainer: {
		marginTop: spacing.md,
		padding: `${spacing.sm}px ${spacing.md}px`,
		background: hexToRGBA(colors.background.light, 0.5),
		borderRadius: common.borderRadius,
		border: THEME.colors.border.light,
	},
	conditionalCheckboxLabel: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		fontSize: fontSizes.sm,
		color: colors.text.main,
	},
	conditionalCheckboxText: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.xs,
		flexWrap: 'wrap',
	},
	conditionalThresholdInput: {
		width: 60,
		margin: `0 ${spacing.xs}px`,
		...CssSelectors.allDescendants(
			{
				width: 40,
				textAlign: 'center',
				fontWeight: 'bold',
				color: colors.text.main,
			},
			'input'
		),
	},
	conditionalCheckboxWrapper: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.xs,
		flexWrap: 'wrap',
	},
	tagOnCompletionContainer: {
		marginTop: `${spacing.lg}px !important`,
	},
});
